import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, Select, FormControl, InputLabel, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Tooltip, Alert,
} from '@mui/material';
import {
  ArrowBack, ExitToApp, LocalHospital, Add, Edit, Delete, Receipt,
  AttachFile, Visibility, Close,
} from '@mui/icons-material';
import { authService, expenseService } from '../../services/api';

const CATEGORIES = [
  'Stationary & Office Supplies',
  'Tea, Snacks & Refreshments',
  'Electricity Bill',
  'Water Bill',
  'Internet & Phone',
  'Rent',
  'Equipment Maintenance',
  'Housekeeping & Cleaning',
  'Medicines & Consumables',
  'Miscellaneous',
];

const PAYMENT_MODES = ['Cash', 'UPI', 'Cheque', 'Bank Transfer'];

const CATEGORY_COLOR = {
  'Stationary & Office Supplies':  '#1565c0',
  'Tea, Snacks & Refreshments':    '#e65100',
  'Electricity Bill':              '#f9a825',
  'Water Bill':                    '#0277bd',
  'Internet & Phone':              '#6a1b9a',
  'Rent':                          '#4e342e',
  'Equipment Maintenance':         '#37474f',
  'Housekeeping & Cleaning':       '#2e7d32',
  'Medicines & Consumables':       '#c62828',
  'Miscellaneous':                 '#546e7a',
};

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const ACCEPTED = '.jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx';
const MAX_SIZE  = 5 * 1024 * 1024; // 5 MB

const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const fmt2    = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '—';

const emptyForm = () => ({
  date:        new Date().toISOString().split('T')[0],
  category:    '',
  description: '',
  amount:      '',
  paymentMode: 'Cash',
  paidTo:      '',
  billNo:      '',
});

export default function ExpensesPage() {
  const navigate  = useNavigate();
  const user      = authService.getCurrentUser();
  const fileInput = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const now = new Date();
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [year,      setYear]      = useState(now.getFullYear());
  const [filterCat, setFilterCat] = useState('');

  const [expenses, setExpenses] = useState([]);
  const [summary,  setSummary]  = useState({ summary: [], grandTotal: 0 });
  const [loading,  setLoading]  = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(emptyForm());
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [formError,  setFormError]  = useState('');

  // Attachment state
  const [selectedFile,      setSelectedFile]      = useState(null);   // new file chosen
  const [existingAttachment, setExistingAttachment] = useState(null); // current saved filename
  const [removeAttachment,  setRemoveAttachment]  = useState(false);  // user asked to remove

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month, year, ...(filterCat ? { category: filterCat } : {}) };
      const [eRes, sRes] = await Promise.all([
        expenseService.getAll(params),
        expenseService.getSummary({ month, year }),
      ]);
      setExpenses(eRes.data);
      setSummary(sRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, year, filterCat]);

  useEffect(() => { load(); }, [load]);

  const handleField = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm());
    setSelectedFile(null);
    setExistingAttachment(null);
    setRemoveAttachment(false);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = row => {
    setEditId(row.id);
    setForm({
      date:        row.date.split('T')[0],
      category:    row.category,
      description: row.description,
      amount:      row.amount,
      paymentMode: row.paymentMode,
      paidTo:      row.paidTo  || '',
      billNo:      row.billNo  || '',
    });
    setSelectedFile(null);
    setExistingAttachment(row.attachmentPath || null);
    setRemoveAttachment(false);
    setFormError('');
    setDialogOpen(true);
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_SIZE) {
      setFormError('File size must not exceed 5 MB.');
      e.target.value = '';
      return;
    }
    setFormError('');
    setSelectedFile(file);
    setRemoveAttachment(false);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleRemoveExisting = () => {
    setExistingAttachment(null);
    setRemoveAttachment(true);
    setSelectedFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const handleSave = async () => {
    if (!form.category || !form.description || !form.amount) return;
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        addedByUserId: user?.id || null,
      };

      let savedId = editId;
      if (editId) {
        await expenseService.update(editId, payload);
      } else {
        const res = await expenseService.create(payload);
        savedId = res.data.id;
      }

      // Upload new file if selected
      if (selectedFile && savedId) {
        await expenseService.uploadAttachment(savedId, selectedFile);
      } else if (removeAttachment && editId) {
        // User explicitly removed the existing attachment
        await expenseService.deleteAttachment(editId);
      }

      setDialogOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to save expense.';
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await expenseService.delete(deleteId);
      setDeleteId(null);
      await load();
    } catch { /* ignore */ }
  };

  const openProof = filename =>
    window.open(`${BASE_URL}/uploads/expenses/${filename}`, '_blank');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Hospital Expenses
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Category</InputLabel>
              <Select value={filterCat} label="Filter by Category" onChange={e => setFilterCat(e.target.value)}>
                <MenuItem value="">All Categories</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ ml: 'auto' }}>
              Add Expense
            </Button>
          </Box>
        </Paper>

        {/* Summary cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #c62828', minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.dark' }}>₹ {fmt2(summary.grandTotal)}</Typography>
            <Typography variant="caption" color="text.secondary">{MONTHS[month - 1]} {year}</Typography>
          </Paper>
          <Paper elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: '4px solid #1565c0', minWidth: 180 }}>
            <Typography variant="caption" color="text.secondary">Entries</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{expenses.length}</Typography>
          </Paper>
          {summary.summary.slice(0, 3).map(s => (
            <Paper key={s.category} elevation={1} sx={{ p: 1.5, px: 2.5, borderRadius: 2, borderLeft: `4px solid ${CATEGORY_COLOR[s.category] || '#546e7a'}`, minWidth: 180 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 160 }}>
                {s.category}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>₹ {fmt2(s.total)}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Category breakdown */}
        {summary.summary.length > 0 && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Category Breakdown — {MONTHS[month - 1]} {year}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {summary.summary.map(s => (
                <Chip
                  key={s.category}
                  label={`${s.category}: ₹${fmt2(s.total)} (${s.count})`}
                  size="small"
                  sx={{ bgcolor: CATEGORY_COLOR[s.category] || '#546e7a', color: '#fff', fontWeight: 600 }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Expense table */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Amount (₹)</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Payment Mode</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Paid To</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Bill No.</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Proof</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((row, idx) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{fmtDate(row.date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.category}
                          size="small"
                          sx={{ bgcolor: CATEGORY_COLOR[row.category] || '#546e7a', color: '#fff', fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt2(row.amount)}</TableCell>
                      <TableCell>
                        <Chip label={row.paymentMode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.paidTo || '—'}</TableCell>
                      <TableCell>{row.billNo || '—'}</TableCell>
                      <TableCell align="center">
                        {row.attachmentPath ? (
                          <Tooltip title="View Bill Proof">
                            <IconButton size="small" color="primary" onClick={() => openProof(row.attachmentPath)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {expenses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No expenses recorded for {MONTHS[month - 1]} {year}.
                      </TableCell>
                    </TableRow>
                  )}
                  {expenses.length > 0 && (
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.dark', fontSize: '1rem' }}>
                        {fmt2(expenses.reduce((s, e) => s + (e.amount || 0), 0))}
                      </TableCell>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt /> {editId ? 'Edit Expense' : 'Add Expense'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '20px !important' }}>

          {formError && (
            <Alert severity="error" onClose={() => setFormError('')}>{formError}</Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date" type="date" size="small" fullWidth
              value={form.date} onChange={handleField('date')}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Payment Mode</InputLabel>
              <Select value={form.paymentMode} label="Payment Mode" onChange={handleField('paymentMode')}>
                {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <FormControl size="small" fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select value={form.category} label="Category" onChange={handleField('category')}>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label="Description" size="small" required
            value={form.description} onChange={handleField('description')}
            placeholder="e.g. A4 paper reams (2 packs)"
          />

          <TextField
            label="Amount" type="number" size="small" required
            value={form.amount} onChange={handleField('amount')}
            inputProps={{ min: 0, step: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Paid To (vendor / person)" size="small" fullWidth
              value={form.paidTo} onChange={handleField('paidTo')}
            />
            <TextField
              label="Bill / Receipt No." size="small" fullWidth
              value={form.billNo} onChange={handleField('billNo')}
            />
          </Box>

          {/* ── Attachment ── */}
          <Box sx={{ border: '1px dashed #bdbdbd', borderRadius: 1, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
              Bill Proof (Image / PDF / Word — max 5 MB)
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {/* Show existing saved attachment (edit mode, not replaced/removed) */}
            {existingAttachment && !selectedFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachFile fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ flex: 1, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => openProof(existingAttachment)}>
                  {existingAttachment}
                </Typography>
                <Tooltip title="View proof">
                  <IconButton size="small" color="primary" onClick={() => openProof(existingAttachment)}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove attachment">
                  <IconButton size="small" color="error" onClick={handleRemoveExisting}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Show newly selected file */}
            {selectedFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachFile fontSize="small" color="success" />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>{selectedFile.name}</Typography>
                <Tooltip title="Remove selected file">
                  <IconButton size="small" color="error" onClick={handleClearFile}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={() => fileInput.current?.click()}
            >
              {existingAttachment && !selectedFile ? 'Replace File' : 'Attach File'}
            </Button>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.category || !form.description || !form.amount}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : editId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this expense record? Any attached proof file will also be removed.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
